/**
 * CreateReportModal Component
 * Modal for creating a new power report with agency selection
 * Features:
 * - Sub-agency selection for GA users with custom dropdown
 * - Hide/unhide agencies functionality inside dropdown
 * - Shows terminated and no inventory warnings
 * - Agency address display
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentPlusIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathSpinner } from '../../../shared/OathUI';
import { useOathAuth } from '../../../../services/oathAuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createReport, getSubAgencies, hideAgency, unhideAgency } from '../../../../services/oathPowerReportService';

const CreateReportModal = ({ isOpen, onClose, onSuccess, agencyId }) => {
  const { user } = useOathAuth();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [isLoadingAgencies, setIsLoadingAgencies] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [hiddenExpanded, setHiddenExpanded] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightedAgencyIndex, setHighlightedAgencyIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const dropdownListRef = useRef(null);

  // Separate visible and hidden agencies
  const visibleAgencies = agencies.filter(a => !a.isHidden);
  const hiddenAgencies = agencies.filter(a => a.isHidden);

  // Check if user has multiple agencies (GA user)
  const hasMultipleAgencies = visibleAgencies.length > 1 || hiddenAgencies.length > 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  // Load agencies when modal opens
  const loadAgencies = useCallback(async () => {
    if (!agencyId) return;

    setIsLoadingAgencies(true);
    try {
      const result = await getSubAgencies(agencyId);
      if (result.success && result.data) {
        setAgencies(result.data);
        // Default to first visible agency (main agency) if not already selected
        const visibleList = result.data.filter(a => !a.isHidden);
        if (visibleList.length > 0) {
          setSelectedAgencyId(prev => {
            // Keep current selection if it's still visible
            if (prev && visibleList.some(a => a.agencyId === prev)) {
              return prev;
            }
            return visibleList[0].agencyId;
          });
        }
      } else {
        // Fall back to user's agencies if sub-agencies API fails
        const userAgencies = user?.agencies || [];
        if (userAgencies.length > 0) {
          const mappedAgencies = userAgencies.map((a) => ({
            agencyId: a.agencyId,
            name: a.dbaAgencyName || a.legalAgencyName || 'Agency',
            isMainAgency: a.isMainAgency || false,
            isHidden: false,
            hasInventory: true,
            isTerminated: false,
            street: a.street,
            city: a.city,
            state: a.state,
            postalCode: a.postalCode
          }));
          setAgencies(mappedAgencies);
          setSelectedAgencyId(agencyId || mappedAgencies[0].agencyId);
        }
      }
    } catch (err) {
      console.error('Failed to load agencies:', err);
      toast.error('Failed to load agencies');
      // Fall back to user's main agency
      const mainAgency = user?.agencies?.find(a => a.isMainAgency) || user?.agencies?.[0];
      if (mainAgency) {
        setAgencies([{
          agencyId: mainAgency.agencyId,
          name: mainAgency.dbaAgencyName || 'Main Agency',
          isMainAgency: true,
          isHidden: false,
          hasInventory: true,
          isTerminated: false
        }]);
        setSelectedAgencyId(mainAgency.agencyId);
      }
    } finally {
      setIsLoadingAgencies(false);
    }
  }, [agencyId, user?.agencies]);

  useEffect(() => {
    if (isOpen) {
      loadAgencies();
    }
  }, [isOpen, loadAgencies]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHiddenExpanded(false);
      setDropdownOpen(false);
      setHighlightedAgencyIndex(-1);
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedAgencyIndex >= 0 && dropdownListRef.current) {
      const items = dropdownListRef.current.children;
      if (items[highlightedAgencyIndex]) {
        items[highlightedAgencyIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedAgencyIndex]);

  // Handle keyboard navigation for agency dropdown
  const handleAgencyKeyDown = useCallback((e) => {
    if (!dropdownOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setDropdownOpen(true);
      setHighlightedAgencyIndex(0);
      return;
    }

    if (!dropdownOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedAgencyIndex(prev =>
          prev < visibleAgencies.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedAgencyIndex(prev =>
          prev > 0 ? prev - 1 : visibleAgencies.length - 1
        );
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (highlightedAgencyIndex >= 0 && highlightedAgencyIndex < visibleAgencies.length) {
          handleSelectAgency(visibleAgencies[highlightedAgencyIndex].agencyId);
        }
        break;
      case 'Tab':
        if (highlightedAgencyIndex >= 0 && highlightedAgencyIndex < visibleAgencies.length) {
          handleSelectAgency(visibleAgencies[highlightedAgencyIndex].agencyId);
        }
        setDropdownOpen(false);
        break;
      case 'Escape':
        e.preventDefault();
        setDropdownOpen(false);
        setHighlightedAgencyIndex(-1);
        break;
      default:
        break;
    }
  }, [dropdownOpen, visibleAgencies, highlightedAgencyIndex]);

  const handleSelectAgency = (targetAgencyId) => {
    setSelectedAgencyId(targetAgencyId);
    setDropdownOpen(false);
    setHighlightedAgencyIndex(-1);
  };

  const getSelectedAgency = () => {
    return agencies.find(a => a.agencyId === selectedAgencyId);
  };

  const handleHideAgency = async (targetAgencyId, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    try {
      const result = await hideAgency(targetAgencyId);
      if (result.success) {
        // Update local state
        setAgencies(prev => prev.map(a =>
          a.agencyId === targetAgencyId ? { ...a, isHidden: true } : a
        ));
        // If hiding the selected agency, select first visible agency
        if (selectedAgencyId === targetAgencyId) {
          const remainingVisible = visibleAgencies.filter(a => a.agencyId !== targetAgencyId);
          setSelectedAgencyId(remainingVisible[0]?.agencyId || null);
        }
        toast.success('Agency hidden');
      } else {
        toast.error(result.error || 'Failed to hide agency');
      }
    } catch (err) {
      console.error('Failed to hide agency:', err);
      toast.error('Failed to hide agency');
    }
  };

  const handleUnhideAgency = async (targetAgencyId, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    try {
      const result = await unhideAgency(targetAgencyId);
      if (result.success) {
        // Update local state
        setAgencies(prev => prev.map(a =>
          a.agencyId === targetAgencyId ? { ...a, isHidden: false } : a
        ));
        toast.success('Agency visible');
      } else {
        toast.error(result.error || 'Failed to unhide agency');
      }
    } catch (err) {
      console.error('Failed to unhide agency:', err);
      toast.error('Failed to unhide agency');
    }
  };

  const handleCreate = async () => {
    if (!selectedAgencyId) {
      toast.error('Please select an agency');
      return;
    }

    setIsCreating(true);

    try {
      const result = await createReport(selectedAgencyId);

      if (result.success && result.data) {
        const newReport = result.data;
        toast.success(`Report ${newReport.reportNumber || ''} created successfully`);
        onClose();
        onSuccess?.();

        // Navigate to the new report details page
        navigate(`/app/power-reports/${newReport.powerReportId}`);
      } else {
        toast.error(result.error || 'Failed to create power report');
      }
    } catch (err) {
      toast.error('Failed to create power report');
      console.error(err);
    } finally {
      setIsCreating(false);
    }
  };

  const selectedAgency = getSelectedAgency();

  // Get display label for selected agency
  const getSelectedLabel = () => {
    if (!selectedAgency) return 'Select an agency';
    let label = `${selectedAgency.agencyId} - ${selectedAgency.name || 'Unknown Agency'}`;
    if (selectedAgency.isMainAgency) label += ' (Main Agency)';
    return label;
  };

  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Power Report"
      size="lg"
      allowOverflow
      footer={
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          <OathButton
            variant="outline"
            onClick={onClose}
            disabled={isCreating}
            className="w-full sm:w-auto"
          >
            Cancel
          </OathButton>
          <OathButton
            onClick={handleCreate}
            disabled={isCreating || isLoadingAgencies || !selectedAgencyId}
            icon={isCreating ? null : DocumentPlusIcon}
            className="w-full sm:w-auto"
          >
            {isCreating ? (
              <span className="flex items-center gap-2">
                <OathSpinner size="sm" />
                Creating...
              </span>
            ) : (
              'Create Report'
            )}
          </OathButton>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          This will create a new power report. You can add powers to the report on the next screen.
        </p>

        {/* Agency Selection */}
        {isLoadingAgencies ? (
          <div className="h-14 bg-gray-100 dark:bg-gray-700 rounded-xl animate-pulse" />
        ) : hasMultipleAgencies ? (
          <div ref={dropdownRef} className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Agency
            </label>

            {/* Custom Dropdown Trigger */}
            <button
              type="button"
              onClick={() => {
                setDropdownOpen(!dropdownOpen);
                if (!dropdownOpen) setHighlightedAgencyIndex(0);
              }}
              onKeyDown={handleAgencyKeyDown}
              disabled={isCreating}
              className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary text-left"
            >
              <div className="flex-1 min-w-0">
                <span className="block truncate">{getSelectedLabel()}</span>
                {selectedAgency && (selectedAgency.isTerminated || !selectedAgency.hasInventory) && (
                  <span className="text-xs text-gray-500">
                    {selectedAgency.isTerminated && 'Terminated'}
                    {selectedAgency.isTerminated && !selectedAgency.hasInventory && ' | '}
                    {!selectedAgency.hasInventory && 'No Inventory'}
                  </span>
                )}
              </div>
              {dropdownOpen ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              )}
            </button>

            {/* Custom Dropdown Menu */}
            {dropdownOpen && (
              <div ref={dropdownListRef} className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-80 overflow-y-auto">
                {/* Visible Agencies */}
                {visibleAgencies.map((agency, index) => (
                  <div
                    key={agency.agencyId}
                    className={`group flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                      agency.agencyId === selectedAgencyId ? 'bg-primary/5 dark:bg-primary/10' : ''
                    } ${index === highlightedAgencyIndex ? 'bg-primary/10 dark:bg-primary/20' : ''}`}
                    onClick={() => handleSelectAgency(agency.agencyId)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* Selection indicator */}
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        agency.agencyId === selectedAgencyId
                          ? 'border-primary bg-primary'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {agency.agencyId === selectedAgencyId && (
                          <CheckIcon className="h-3 w-3 text-white" />
                        )}
                      </div>

                      {/* Agency info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {agency.agencyId} - {agency.name}
                          </span>
                          {agency.isMainAgency && (
                            <span className="text-xs bg-primary/10 text-primary dark:text-primary-light px-2 py-0.5 rounded-full flex-shrink-0">
                              Main
                            </span>
                          )}
                        </div>
                        {(agency.isTerminated || !agency.hasInventory) && (
                          <div className="flex items-center gap-2 mt-0.5">
                            {agency.isTerminated && (
                              <span className="text-xs text-red-500">Terminated</span>
                            )}
                            {!agency.hasInventory && (
                              <span className="text-xs text-gray-400">No Inventory</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Hide button - only show for non-main agencies */}
                    {!agency.isMainAgency && (
                      <button
                        type="button"
                        onClick={(e) => handleHideAgency(agency.agencyId, e)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        title="Hide agency"
                      >
                        <EyeSlashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : visibleAgencies.length === 1 ? (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium text-gray-900 dark:text-white">Agency: </span>
              {visibleAgencies[0]?.agencyId} - {visibleAgencies[0]?.name || user?.agencies?.[0]?.dbaAgencyName || 'N/A'}
              {!visibleAgencies[0]?.hasInventory && (
                <span className="ml-1 text-gray-400">- No Inventory</span>
              )}
            </p>
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium text-gray-900 dark:text-white">Agency: </span>
              {user?.agencies?.[0]?.agencyId} - {user?.agencies?.[0]?.dbaAgencyName || 'N/A'}
            </p>
          </div>
        )}

        {/* Hidden Agencies Section - Separate from main dropdown */}
        {hiddenAgencies.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setHiddenExpanded(!hiddenExpanded)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center gap-2">
                <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Hidden Agencies
                </span>
                <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full">
                  {hiddenAgencies.length}
                </span>
              </div>
              {hiddenExpanded ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              )}
            </button>

            {hiddenExpanded && (
              <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 max-h-48 overflow-y-auto">
                {hiddenAgencies.map((agency, index) => (
                  <div
                    key={agency.agencyId}
                    className={`flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      index !== hiddenAgencies.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate block">
                        {agency.agencyId} - {agency.name}
                      </span>
                      {(agency.isTerminated || !agency.hasInventory) && (
                        <span className="text-xs text-gray-400">
                          {[
                            agency.isTerminated && 'Terminated',
                            !agency.hasInventory && 'No Inventory'
                          ].filter(Boolean).join(' | ')}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => handleUnhideAgency(agency.agencyId, e)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary dark:text-primary-light hover:bg-primary/10 rounded-lg transition-colors flex-shrink-0"
                      title="Show agency"
                    >
                      <EyeIcon className="h-4 w-4" />
                      <span>Show</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Show selected agency address when multiple agencies exist */}
        {hasMultipleAgencies && selectedAgency && (selectedAgency.street || selectedAgency.city) && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {selectedAgency.street && <>{selectedAgency.street}<br /></>}
              {selectedAgency.city && selectedAgency.state && (
                <>{selectedAgency.city}, {selectedAgency.state} {selectedAgency.postalCode}</>
              )}
            </p>
          </div>
        )}

        {/* Agent Info */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium text-gray-900 dark:text-white">Agent: </span>
            {user?.agencies?.find(a => a.agencyId === selectedAgencyId)?.primaryContact
              || `${user?.firstName || ''} ${user?.lastName || ''}`}
          </p>
        </div>
      </div>
    </OathModal>
  );
};

export default CreateReportModal;
